/** @format */

import React, {useEffect, useState} from 'react';

import clsx from 'clsx';
import {Trans, useTranslation} from 'react-i18next';
import {Box, Typography, LinearProgress, Button, Checkbox} from '@mui/material';
import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';

import Modal from 'components/Modal';
import LegacyTextField from 'components/inputs/LegacyTextField';
import Breadcrumbs from 'components/Breadcrumbs';
import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';

import {ROUTES} from 'constants/ROUTES';

import {classes, Root} from './styles';
import {
  CategoryType,
  CategoryTypeWithNew,
  CompanyIdsList,
  CompanyType,
  CompanyTypeWithChecked,
} from './types';
import {generateId} from './helpers/generateId';
import {
  useCreateCategoryMutation,
  useGetCurrentUserQuery,
  useRemoveCategoryMutation,
  useUpdateCategoryMutation,
} from './graphql/__generated__';

const Grouping = () => {
  const {t} = useTranslation();

  const [categories, setCategories] = useState<CategoryTypeWithNew[] | ReadonlyArray<CategoryType>>(
    [],
  );
  const [companies, setCompanies] = useState<CompanyTypeWithChecked[]>([]);
  const [companiesBackup, setCompaniesBackup] = useState<CompanyTypeWithChecked[]>([]);
  const [companyIds, setCompanyIds] = useState<CompanyIdsList>({});
  const [currentTagId, setCurrentTagId] = useState<null | string>(null);
  const [editCategoryId, setEditCategoryId] = useState<null | string>(null);
  const [isTagsModalVisible, setIsTagsModalVisible] = useState<boolean>(false);
  const [removedTagIds, setRemovedTagIds] = useState<string[]>([]);

  const {loading, error, data, refetch} = useGetCurrentUserQuery();
  const [createCategoryError, setCreateCategoryError] = useState('');
  const [removeCategoryError, setRemoveCategoryError] = useState('');
  const [createCategory, {loading: createCategoryLoading}] = useCreateCategoryMutation();
  const [updateCategory, {loading: updateCategoryLoading}] = useUpdateCategoryMutation();
  const [removeCategory, {loading: removeCategoryLoading}] = useRemoveCategoryMutation();

  useEffect(() => {
    const currentUserCompanies = data?.currentUser?.companies;
    const currentUserCategories = data?.currentUser?.categories;
    if (!currentUserCompanies || !currentUserCategories) {
      return;
    }

    setCategories(currentUserCategories);
    setCompanies(
      currentUserCompanies.map(v => ({
        ...v,
        isChecked: false,
      })),
    );
  }, [data]);

  useEffect(() => {
    if (categories.length) {
      const map = companies.reduce((acc, company) => {
        const companyTags = (company.tags || []).filter(v => v.id);
        companyTags.forEach(tag => {
          if (!tag.id) return;
          if (!acc[tag.id]) {
            acc[tag.id] = [];
          }
          if (acc[tag.id]) {
            acc[tag.id].push(company.id);
          } else {
            acc[tag.id] = [company.id];
          }
        });
        return acc;
      }, {} as CompanyIdsList);

      const editCategory = categories.find(v => v.id === editCategoryId);
      if (editCategory) {
        editCategory.tags?.forEach(tag => {
          map[tag.id] = companyIds[tag.id] || [];
        });
      }

      setCompanyIds(map);
    }
    // Если исправить эту ошибку, то попадем в бесконечный цикл
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  const breadcrumbs = [
    {
      href: `/${ROUTES.GROUPING}`,
      title: <Trans>Проекты</Trans>,
    },
    {
      href: '',
      title: <Trans>Настройка группировок</Trans>,
    },
  ];

  const handleClickAddNew = () => {
    const newCategoryId = generateId();

    setCategories([
      {
        id: newCategoryId,
        name: 'Новая группа',
        isNew: true,
        tags: [{id: generateId(), isNew: true, name: 'Новая папка', companyIds: []}],
      },
      ...categories,
    ]);

    setCompaniesBackup([...companies]);
    setEditCategoryId(newCategoryId);
  };

  if (createCategoryLoading || updateCategoryLoading || removeCategoryLoading) {
    return <LinearProgress style={{flex: 1}} />;
  }

  if (createCategoryError || removeCategoryError) {
    return <Alert severity='error'>{createCategoryError || removeCategoryError}</Alert>;
  }

  if (loading || error) {
    return null;
  }

  const handleClickSaveCategory = async (id: string) => {
    const category = (categories as CategoryTypeWithNew[]).find(v => v.id === id);

    if (!category) return;

    if (category.isNew) {
      await createCategory({
        variables: {
          category: {
            name: category.name,
            tags: category.tags?.map(tag => ({
              name: tag.name,
              companyIds: tag.id ? companyIds[tag.id].map(v => Number(v)) : [],
            })),
          },
        },
      })
        .then(() => {
          refetch();
        })
        .catch(e => setCreateCategoryError((e as Error).message));
    } else {
      const createdTags = category.tags
        ?.filter(v => v.isNew)
        .map(tag => {
          return {
            name: tag.name,
            companyIds: (companyIds[Number(tag.id)] || []).map(v => Number(v)),
          };
        });

      const updatedTags = category.tags
        ?.filter(v => !v.isNew)
        .map(tag => {
          return {
            id: Number(tag.id),
            name: tag.name,
            companyIds: (companyIds[Number(tag.id)] || []).map(v => Number(v)),
          };
        });

      updateCategory({
        variables: {
          category: {
            id: Number(category.id),
            name: category.name,
            removedTags: removedTagIds.map(v => ({id: Number(v)})),
            createdTags: createdTags,
            updatedTags: updatedTags,
          },
        },
      })
        .then(() => {
          refetch();

          setEditCategoryId(null);
        })
        .catch(e => setCreateCategoryError((e as Error).message));
    }
  };

  const handleCategoryNameChange = (value: string, id: string) => {
    const oldCategory = categories.find(v => v.id === id);
    if (!oldCategory) return;

    const updatedCategory: CategoryType = {
      ...oldCategory,
      name: value,
    };

    if (updatedCategory) {
      setCategories(
        categories.map(category => {
          if (category.id === id) {
            return updatedCategory;
          }

          return category;
        }),
      );
    }
  };

  const handleSelectCompany = (e: React.ChangeEvent<HTMLInputElement>, companyId: string) => {
    if (!currentTagId) return;

    const tagCompanyIds = companyIds[currentTagId] || [];
    if (e.target.checked) {
      const updatedArray = [...tagCompanyIds, companyId];

      setCompanyIds({
        ...companyIds,
        ...{
          [currentTagId]: updatedArray,
        },
      });
    } else {
      const updatedArray = tagCompanyIds.filter(id => id !== companyId);

      setCompanyIds({
        ...companyIds,
        ...{
          [currentTagId]: updatedArray,
        },
      });
    }
  };

  const handleTagNameChange = (value: string, categoryId: string, tagId: string) => {
    const category = categories.find(v => v.id === categoryId);

    if (category) {
      const updatedCategory = {
        ...category,
        tags: category.tags?.map(tag => {
          if (tag.id === tagId) {
            return {
              ...tag,
              name: value,
            };
          }

          return tag;
        }),
      };

      setCategories(
        categories.map(v => {
          if (v.id === categoryId) {
            return updatedCategory;
          }
          return v;
        }),
      );
    }
  };

  const handleTagDelete = (categoryId: string, tagId: string) => {
    const category = (categories as CategoryTypeWithNew[]).find(v => v.id === categoryId);

    if (category) {
      const updatedCategory = {
        ...category,
        tags: category.tags?.filter(v => v.id !== tagId),
      };

      const tag = category.tags?.find(v => v.id === tagId);
      if (!tag) return;

      if (!tag.isNew) {
        setRemovedTagIds([...removedTagIds, tagId]);
      }

      setCategories(
        categories.map(v => {
          if (v.id === categoryId) {
            return updatedCategory;
          }
          return v;
        }),
      );
    }
  };

  const handleCategoryDelete = async (id: string) => {
    const category = (categories as CategoryTypeWithNew[]).find(v => v.id === id);

    if (!category?.isNew) {
      await removeCategory({
        variables: {
          category: {
            id: Number(id),
          },
        },
      })
        .then(() => {
          handleTagsModalClose();

          refetch();
        })
        .catch(e => setRemoveCategoryError(e));
    } else {
      setCategories(categories.filter(v => v.id !== id));
    }
  };

  const handleCompanyDelete = (id: string, tagId: string) => {
    const newCompanyIds = companyIds[tagId].filter(v => v !== id);
    setCompanyIds({
      ...companyIds,
      ...{
        [tagId]: newCompanyIds,
      },
    });
  };

  const handleAddCompany = (categoryId: string, tagId: string) => {
    const category = (categories as CategoryTypeWithNew[]).find(v => v.id === categoryId);

    if (category) {
      setIsTagsModalVisible(true);
    }

    setCurrentTagId(tagId);
  };

  const handleAddTag = (categoryId: string) => {
    const category = (categories as CategoryTypeWithNew[]).find(v => v.id === categoryId);

    if (category && category.tags) {
      const updatedCategory: CategoryTypeWithNew = {
        ...category,
        tags: [...category.tags, {id: generateId(), isNew: true, name: '', companyIds: []}],
      };

      setCategories(
        categories.map(v => {
          if (v.id === categoryId) {
            return updatedCategory;
          }
          return v;
        }),
      );
    }
  };

  const handleTagsModalClose = () => {
    setIsTagsModalVisible(false);

    setCurrentTagId(null);
  };

  const handleEditCategoryClick = (id: string) => {
    setCompaniesBackup([...companies]);
    setEditCategoryId(id || null);
  };

  const handleResetEditMode = () => {
    setCompanies(companiesBackup);
    setCompaniesBackup([]);
    setEditCategoryId(null);
  };

  return (
    <Root>
      <Box
        sx={{
          mb: 3,
        }}
      >
        <Breadcrumbs items={breadcrumbs} />
      </Box>
      <Box
        sx={{
          mt: 10,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography className={classes.headerTitle}>
            <Trans>Режимы группировки</Trans>
          </Typography>
          <PrimaryMediumButton
            variant='contained'
            onClick={handleClickAddNew}
            className={classes.addButton}
            data-testid='add-new-category-button'
          >
            <Box
              sx={{
                mr: 2,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <AddCircleOutlineRounded className={classes.addButtonIcon} />
            </Box>
            <Trans>Добавить</Trans>
          </PrimaryMediumButton>
        </Box>
      </Box>
      <Box
        sx={{
          mt: 6,
        }}
      >
        {categories.length ? (
          categories.map(category => {
            let isEditMode = editCategoryId === category.id;
            return (
              <Box
                key={category.id}
                className={clsx(
                  classes.groupContainer,
                  isEditMode && classes.groupContainerEditMode,
                )}
              >
                <Box className={classes.groupHeader}>
                  {isEditMode ? (
                    <LegacyTextField
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleCategoryNameChange(e.target.value, category.id)
                      }
                      placeholder={category.name}
                      className={classes.groupNameInput}
                      slotProps={{
                        htmlInput: {
                          'data-testid': 'change-name-input',
                        },
                      }}
                    />
                  ) : (
                    <Typography className={classes.groupTitle}>{category.name}</Typography>
                  )}

                  {isEditMode ? (
                    <Box>
                      <PrimaryMediumButton
                        variant='contained'
                        onClick={() => handleClickSaveCategory(category.id)}
                        className={classes.saveGroupEditButton}
                        data-testid='save-category-button'
                      >
                        <Trans>Сохранить</Trans>
                      </PrimaryMediumButton>

                      <Button
                        onClick={handleResetEditMode}
                        className={classes.resetGroupEditButton}
                      >
                        <Trans>Отменить</Trans>
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      className={classes.groupEditButton}
                      onClick={() => handleEditCategoryClick(category.id)}
                      data-testid='edit-category-button'
                    >
                      <EditIcon className={classes.groupEditButtonIcon} />
                    </Button>
                  )}
                </Box>

                <Box className={classes.groupBody}>
                  <Box className={clsx(classes.groupHeadingRow, classes.borderBottom)}>
                    <Box>
                      <Trans>Папки</Trans>
                    </Box>
                    <Box>
                      <Trans>Проекты</Trans>
                    </Box>
                  </Box>

                  {category.tags &&
                    category.tags.map(tag => {
                      let tagCompanies: CompanyType[] = [];

                      companyIds[tag.id]?.forEach(companyId => {
                        const foundCompany = companies.find(company => company.id === companyId);
                        if (foundCompany) {
                          tagCompanies.push(foundCompany);
                        }
                      });

                      return (
                        <React.Fragment key={tag.id}>
                          <Box className={clsx(classes.groupRow, classes.borderBottom)}>
                            <Box>
                              {isEditMode ? (
                                <LegacyTextField
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    handleTagNameChange(e.target.value, category.id, tag.id)
                                  }
                                  data-testid='change-tag-name-input'
                                  placeholder={tag.name}
                                />
                              ) : (
                                <Typography>{tag.name}</Typography>
                              )}
                              {tagCompanies && (
                                <Box className={classes.projectsAmount}>
                                  {t('plurals.companies', {
                                    count: tagCompanies.length,
                                  })}
                                </Box>
                              )}
                              {isEditMode && (
                                <Button
                                  onClick={() => handleTagDelete(category.id, tag.id)}
                                  className={classes.deleteFolderButton}
                                  data-testid='delete-tag-button'
                                >
                                  <Trans>Удалить папку</Trans>
                                </Button>
                              )}
                            </Box>
                            <Box>
                              {tagCompanies.map(v => {
                                return (
                                  <Box
                                    key={v.id}
                                    className={clsx(
                                      classes.projectContainer,
                                      isEditMode && classes.projectContainerEditable,
                                    )}
                                  >
                                    {v.name}

                                    {isEditMode && (
                                      <Button
                                        className={classes.projectDeleteButton}
                                        onClick={() => handleCompanyDelete(v.id, tag.id)}
                                        data-testid='company-delete-button'
                                      >
                                        <CloseIcon className={classes.projectDeleteButtonIcon} />
                                      </Button>
                                    )}
                                  </Box>
                                );
                              })}

                              {isEditMode && (
                                <Button
                                  onClick={() => handleAddCompany(category.id, tag.id)}
                                  data-testid='add-new-company-button'
                                  className={classes.addProjectButton}
                                >
                                  <AddIcon className={classes.addProjectButtonIcon} />
                                  <Trans>Добавить компанию</Trans>
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </React.Fragment>
                      );
                    })}

                  {isTagsModalVisible && (
                    <Modal
                      header={category.name}
                      onClose={handleTagsModalClose}
                      showCloseIcon
                      data-testid='tag-modal'
                      footer={
                        <Box>
                          <PrimaryMediumButton onClick={handleTagsModalClose}>
                            <Trans>Сохранить</Trans>
                          </PrimaryMediumButton>
                        </Box>
                      }
                    >
                      <Box>
                        {companies.map(company => {
                          return (
                            <Box key={company.id} maxHeight='70vh'>
                              <Checkbox
                                edge='start'
                                color='default'
                                defaultChecked={company.isChecked}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  handleSelectCompany(e, company.id);
                                }}
                              />
                              {company.name}
                            </Box>
                          );
                        })}
                      </Box>
                    </Modal>
                  )}

                  {isEditMode && (
                    <Button
                      onClick={() => handleAddTag(category.id)}
                      className={classes.addProjectButton}
                      data-testid='add-new-tag-button'
                    >
                      <AddIcon className={classes.addProjectButtonIcon} />
                      <Trans>Добавить папку</Trans>
                    </Button>
                  )}

                  {isEditMode && (
                    <Button
                      onClick={() => handleCategoryDelete(category.id)}
                      className={classes.deleteFolderButton}
                      data-testid='delete-category-button'
                    >
                      <Trans>Удалить группу</Trans>
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })
        ) : (
          <Trans>Нет созданных категорий</Trans>
        )}
      </Box>
    </Root>
  );
};

export default Grouping;
